import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Product, ProductPriceResponse } from '../../types/product'
import { LikedProduct } from '../../types/user'
import { getDefaultVariant, getEffectivePrice } from '../../utils/cartUtils'
import { mapLikedApiProductToWishlistItem } from './wishlistMapping'

export interface LikedItem {
  id: string
  name: string
  price: number
  mainPicture?: string
  slug: string
  description?: string
  beanType?: { label: string; value: string }
  originCountry?: string
  originRegion?: string
  prices?: ProductPriceResponse[]
  variants?: Array<{ label: string; value: string }>
  tags?: string[]
  likedAt?: string
}

interface LikedState {
  items: LikedItem[]
  isLoading: boolean
  error: string | null
}

// Функції для роботи з localStorage
const loadLikedFromLocalStorage = (): LikedItem[] => {
  try {
    const serializedLiked = localStorage.getItem('liked')
    return serializedLiked ? JSON.parse(serializedLiked) : []
  } catch (e) {
    return []
  }
}

const saveLikedToLocalStorage = (liked: LikedItem[]) => {
  try {
    const serializedLiked = JSON.stringify(liked)
    localStorage.setItem('liked', serializedLiked)
  } catch (e) {
    // Silently fail
  }
}

const initialState: LikedState = {
  items: loadLikedFromLocalStorage(),
  isLoading: false,
  error: null,
}

const likedSlice = createSlice({
  name: 'liked',
  initialState,
  reducers: {
    addToLiked: (state, action: PayloadAction<Product>) => {
      const product = action.payload
      const existingItem = state.items.find(item => item.id === product.id)

      if (!existingItem) {
        const defaultVariant = getDefaultVariant(product.prices || [])
        const likedItem: LikedItem = {
          id: product.id,
          name: product.name,
          price: defaultVariant ? getEffectivePrice(defaultVariant) : 0,
          mainPicture: product.mainPicture,
          slug: product.slug,
          description: product.description,
          beanType: product.beanType,
          originCountry: product.originCountry,
          originRegion: product.originRegion,
          prices: product.prices,
          variants: product.variants,
          tags: product.tags?.map(tag => (typeof tag === 'string' ? tag : tag.name)) || undefined,
          likedAt: new Date().toISOString(),
        }
        state.items.push(likedItem)
        saveLikedToLocalStorage(state.items)
      }
    },
    removeFromLiked: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload)
      saveLikedToLocalStorage(state.items)
    },
    clearLiked: state => {
      state.items = []
      saveLikedToLocalStorage(state.items)
    },
    setLikedItems: (state, action: PayloadAction<LikedItem[]>) => {
      state.items = action.payload
      saveLikedToLocalStorage(state.items)
    },
    setLikedFromApi: (state, action: PayloadAction<LikedProduct[]>) => {
      const likedItems: LikedItem[] = action.payload.map(likedProduct => ({
        ...mapLikedApiProductToWishlistItem(likedProduct),
        likedAt: new Date().toISOString(),
      }))
      state.items = likedItems
      saveLikedToLocalStorage(state.items)
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
  },
})

export const {
  addToLiked,
  removeFromLiked,
  clearLiked,
  setLikedItems,
  setLikedFromApi,
  setLoading,
  setError,
} = likedSlice.actions

export default likedSlice.reducer

// Selectors
export const selectLikedItems = (state: { liked: LikedState }) => state.liked.items
export const selectLikedLoading = (state: { liked: LikedState }) => state.liked.isLoading
export const selectLikedError = (state: { liked: LikedState }) => state.liked.error
export const selectIsLiked = (productId: string) => (state: { liked: LikedState }) =>
  state.liked.items.some(item => item.id === productId)
export const selectLikedCount = (state: { liked: LikedState }) => state.liked.items.length
